import type { ReactNode } from 'react'
import type { IconName } from '../../components/icons/Icon'
import type { HelpContentId } from '../../help/helpContent'
import { PageHeader, type PageHeaderPrimaryAction } from './PageHeader'
import { ListControlsBar } from './ListControlsBar'
import { DataGrid } from './DataGrid'
import { BulkActionBar } from './BulkActionBar'
import { LoadingSkeletonRows } from './states/LoadingSkeletonRows'
import { EmptyState } from './states/EmptyState'
import { ErrorState } from './states/ErrorState'
import type { ActiveFilterChip } from './FilterPanel'
import type {
  BulkActionDef,
  ColumnDef,
  Density,
  ExportFormat,
  ListState,
  QuickFilterDef,
  SavedViewsConfig,
  SortSpec,
} from './types'

export interface ListViewEmptyTenantSpec {
  icon: IconName
  title: string
  description?: string | undefined
  action?: ReactNode | undefined
}

export interface ListViewProps<T> {
  // Identity / header
  entityIcon: IconName
  title: string
  countLabel?: string | undefined
  primaryAction?: PageHeaderPrimaryAction | undefined
  headerSecondaryActions?: ReactNode | undefined
  /** Optional — renders a contextual `HelpBalloon` next to the page title (roadmap #12). */
  helpId?: HelpContentId | undefined

  // Controls
  quickFilters: QuickFilterDef[]
  activeQuickFilterIds: Set<string>
  onQuickFilterToggle: (id: string) => void
  search: string
  onSearchChange: (value: string) => void
  searchLabel: string
  searchPlaceholder?: string | undefined
  filterChildren: ReactNode
  activeFilterCount: number
  activeFilterChips: ActiveFilterChip[]
  onRemoveFilterChip: (id: string) => void
  onClearFilters: () => void
  savedViews: SavedViewsConfig
  /** Optional — see `ListControlsBarProps.onExport` javadoc (IB-98 fix: no dead Export CTA). */
  onExport?: ((format: ExportFormat) => void) | undefined
  /** Reporting & Exports honesty rule — see `ExportMenuProps.note`. */
  exportNote?: string | undefined

  // Columns / sort / density (state lives in the caller so it can persist)
  columns: ColumnDef<T>[]
  columnOrder: string[]
  hiddenColumnIds: Set<string>
  pinnedColumnIds: Set<string>
  onToggleColumnHidden: (id: string) => void
  onMoveColumn: (id: string, direction: -1 | 1) => void
  onToggleColumnPin: (id: string) => void
  sort: SortSpec[]
  onSortChange: (next: SortSpec[]) => void
  density: Density
  onDensityChange: (density: Density) => void
  /**
   * Persisted per-user-per-list resize widths (IB-27 S3) — optional; omit for a screen that
   * hasn't retrofitted persistence (resize still works for the session via `DataGrid`'s internal
   * live-drag preview, see `DataGridProps.columnWidths` javadoc).
   */
  columnWidths?: Record<string, number> | undefined
  onColumnWidthChange?: ((columnId: string, width: number) => void) | undefined

  // Data + state
  state: ListState
  rows: T[]
  getRowId: (row: T) => string
  onRowActivate?: ((row: T) => void) | undefined
  renderRowActions?: ((row: T) => ReactNode) | undefined

  // Selection + bulk
  selection: Set<string>
  onSelectionChange: (next: Set<string>) => void
  bulkActions: BulkActionDef[]

  // Loading / error / empty specifics
  errorMessage?: string | undefined
  onRetry?: (() => void) | undefined
  emptyTenant: ListViewEmptyTenantSpec
  emptyFilteredDescription?: string | undefined

  // Pagination (cursor-based "load more")
  hasMore: boolean
  onLoadMore: () => void
  loadingMore?: boolean | undefined
}

/**
 * The generic list shell: PageHeader + ListControlsBar + DataGrid +
 * pagination + the four states. Every module's list screen instantiates
 * this the way `AccountsListScreen` does — it owns no entity knowledge.
 */
export function ListView<T>(props: ListViewProps<T>) {
  const visibleColumns = props.columnOrder
    .map((id) => props.columns.find((c) => c.id === id))
    .filter((c): c is ColumnDef<T> => Boolean(c) && !props.hiddenColumnIds.has(c!.id))

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <PageHeader
        icon={props.entityIcon}
        title={props.title}
        countLabel={props.countLabel}
        primaryAction={props.primaryAction}
        secondaryActions={props.headerSecondaryActions}
        helpId={props.helpId}
      />
      <ListControlsBar
        quickFilters={props.quickFilters}
        activeQuickFilterIds={props.activeQuickFilterIds}
        onQuickFilterToggle={props.onQuickFilterToggle}
        search={props.search}
        onSearchChange={props.onSearchChange}
        searchLabel={props.searchLabel}
        searchPlaceholder={props.searchPlaceholder}
        filterChildren={props.filterChildren}
        activeFilterCount={props.activeFilterCount}
        activeFilterChips={props.activeFilterChips}
        onRemoveFilterChip={props.onRemoveFilterChip}
        onClearFilters={props.onClearFilters}
        columns={props.columns}
        sort={props.sort}
        onSortChange={props.onSortChange}
        columnOrder={props.columnOrder}
        hiddenColumnIds={props.hiddenColumnIds}
        pinnedColumnIds={props.pinnedColumnIds}
        onToggleColumnHidden={props.onToggleColumnHidden}
        onMoveColumn={props.onMoveColumn}
        onToggleColumnPin={props.onToggleColumnPin}
        columnWidths={props.columnWidths}
        onColumnWidthChange={props.onColumnWidthChange}
        savedViews={props.savedViews}
        density={props.density}
        onDensityChange={props.onDensityChange}
        onExport={props.onExport}
        exportNote={props.exportNote}
      />

      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {props.state === 'error' ? (
          <ErrorState message={props.errorMessage ?? 'Something went wrong loading this list.'} onRetry={() => props.onRetry?.()} />
        ) : props.state === 'empty-tenant' ? (
          <EmptyState
            icon={props.emptyTenant.icon}
            title={props.emptyTenant.title}
            description={props.emptyTenant.description}
            action={props.emptyTenant.action}
          />
        ) : props.state === 'empty-filtered' ? (
          <EmptyState
            icon={props.entityIcon}
            title={`No ${props.title.toLowerCase()} match your filters`}
            description={props.emptyFilteredDescription ?? 'Try removing a filter or clearing your search.'}
            action={
              <button type="button" className="iw-btn iw-btn--sm" onClick={props.onClearFilters}>
                Clear filters
              </button>
            }
          />
        ) : props.state === 'loading' ? (
          <div role="grid" aria-busy="true" aria-label={`${props.title} loading`} className="iw-grid">
            <LoadingSkeletonRows
              columns={visibleColumns.map((c) => ({ id: c.id, width: c.width }))}
              rowHeight={props.density === 'compact' ? 32 : 44}
              hasSelectionColumn
            />
          </div>
        ) : (
          <DataGrid
            columns={visibleColumns}
            rows={props.rows}
            getRowId={props.getRowId}
            density={props.density}
            sort={props.sort}
            onSortChange={props.onSortChange}
            selection={props.selection}
            onSelectionChange={props.onSelectionChange}
            onRowActivate={props.onRowActivate}
            renderRowActions={props.renderRowActions}
            ariaLabel={props.title}
            columnWidths={props.columnWidths}
            onColumnWidthChange={props.onColumnWidthChange}
          />
        )}
      </div>

      {props.state === 'ready' ? (
        <BulkActionBar
          selectedIds={[...props.selection]}
          actions={props.bulkActions}
          onClearSelection={() => props.onSelectionChange(new Set())}
        />
      ) : null}

      {props.state === 'ready' && props.hasMore ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--iw-space-4)', borderTop: '1px solid var(--iw-border)' }}>
          <button type="button" className="iw-btn" onClick={props.onLoadMore} disabled={props.loadingMore}>
            {props.loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      ) : null}
    </div>
  )
}
